import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import Consultation from "@/components/sections/Consultation";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import { Link } from "@/lib/router-compat";
import {
  RelatedPages,
  allEducationLinks,
  allTreatmentsLinks,
  allServiceLinks,
  allTmsByConditionLinks,
} from "@/components/RelatedPages";
import {
  Brain,
  CheckCircle2,
  ShieldCheck,
  Zap,
  Activity,
  HeartPulse,
  Sparkles,
  ArrowRight,
  HelpCircle,
  BarChart3,
  ExternalLink,
} from "lucide-react";
const WhatIsTms = () => {
  const benefits = [
    {
      title: "Non-Systemic",
      description:
        "Nothing enters the bloodstream. Avoids systemic side effects entirely.",
      icon: ShieldCheck,
    },
    {
      title: "Non-Sedating",
      description:
        "You remain fully awake and alert during treatment and can drive yourself home immediately.",
      icon: Activity,
    },
    {
      title: "Non-Invasive",
      description:
        "Nothing is inserted, implanted, or surgically introduced into the body.",
      icon: Brain,
    },
    {
      title: "Non-Drug",
      description:
        "No drug-related side effects such as weight gain, drowsiness, sleeplessness, or sexual problems.",
      icon: Zap,
    },
    {
      title: "FDA Cleared & Proven",
      description:
        "FDA-cleared with 2 out of 3 patients experiencing significant symptom reduction in clinical trials.",
      icon: HeartPulse,
    },
    {
      title: "Covered by Insurance",
      description:
        "Covered by most major commercial insurance plans, Medicare, and Veterans Affairs (VA).",
      icon: Sparkles,
    },
  ];
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={"What Is TMS Therapy? | Neuro Wellness TMS"}
        description={
          "Learn what Transcranial Magnetic Stimulation (TMS) is. FDA-cleared, non-invasive, drug-free depression treatment in Coral Springs, FL. Discover benefits, brain circuitry, and clinical remission."
        }
        canonical={"/contents/tms/what-is-tms-therapy"}
        keywords={
          "What is TMS therapy, Transcranial Magnetic Stimulation Coral Springs, how TMS works depression, non-invasive brain stimulation, FDA cleared TMS Florida, neurotransmitter stimulation"
        }
        schemaJson={[
          {
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            name: "What Is TMS Therapy?",
            description:
              "FDA-cleared Transcranial Magnetic Stimulation (TMS) explanation, benefits, neurotransmitter activation, and non-invasive mood regulation.",
            url: "/contents/tms/what-is-tms-therapy",
            about: {
              "@type": "MedicalTherapy",
              name: "Transcranial Magnetic Stimulation (TMS)",
              code: {
                "@type": "MedicalCode",
                code: "TMS",
                codingSystem: "FDA",
              },
            },
          },
        ]}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* Hero Banner */}
        <section className="relative overflow-hidden bg-gradient-navy text-primary-foreground py-16 sm:py-24">
          <div className="pointer-events-none absolute -right-20 top-0 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
          <div className="container-px mx-auto max-w-7xl">
            <div className="max-w-3xl space-y-4 animate-fade-up">
              <span className="inline-block rounded-full bg-accent/20 px-4 py-1.5 text-xs sm:text-sm font-bold text-accent uppercase tracking-wider">
                TMS Education • Overview
              </span>
              <h1 className="font-headings text-3xl font-extrabold text-white sm:text-5xl md:text-6xl">
                What Is TMS Therapy?
              </h1>
              <p className="text-lg sm:text-2xl text-accent font-semibold">
                A Technological Breakthrough for Major Depression, Anxiety &
                PTSD
              </p>
              <p className="text-base sm:text-lg text-primary-foreground/80 leading-relaxed max-w-2xl">
                Transcranial Magnetic Stimulation (TMS) is an FDA-cleared,
                non-invasive outpatient treatment that stimulates specific brain
                regions involved in mood regulation using magnetic pulses.
              </p>
              <div className="pt-4 flex flex-wrap gap-4">
                <Button
                  asChild
                  size="lg"
                  className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
                >
                  <a href="#consultation">
                    Schedule Complimentary Consultation
                  </a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-2 border-white/50 text-white bg-transparent hover:bg-white/10"
                >
                  <a href="tel:9548279793">Call 954-827-9793</a>
                </Button>
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 overflow-hidden leading-none pointer-events-none">
            <svg
              className="relative block w-full h-8 sm:h-12 text-background"
              viewBox="0 0 1200 120"
              preserveAspectRatio="none"
              fill="currentColor"
            >
              <path d="M0,0 C150,90 350,-40 500,50 C650,140 900,10 1200,40 L1200,120 L0,120 Z"></path>
            </svg>
          </div>
        </section>

        {/* Main Content Section */}
        <section className="py-12 sm:py-20 bg-background">
          <div className="container-px mx-auto max-w-5xl space-y-12">
            {/* Detailed Explanation */}
            <div className="space-y-6 text-foreground/90 text-base sm:text-lg leading-relaxed">
              <div className="rounded-2xl bg-card border border-border p-6 sm:p-10 shadow-soft space-y-5">
                <h2 className="font-headings text-2xl sm:text-3xl font-extrabold text-primary">
                  How Transcranial Magnetic Stimulation Works
                </h2>
                <p>
                  Transcranial Magnetic Stimulation (TMS) is a technological
                  breakthrough in the treatment of Major Depression, PTSD,
                  Anxiety, and other psychiatric disorders. It is an
                  FDA-cleared, non-invasive treatment recognized by the American
                  Psychiatric Association in its Best Practice Guidelines as an
                  effective intervention for depression.
                </p>
                <p>
                  For individuals diagnosed with Major Depressive Disorder (MDD)
                  who have not experienced satisfactory improvement from
                  antidepressant medication, TMS provides a proven pathway
                  toward remission without systemic drug exposure.
                </p>
                <p>
                  TMS therapy differs from traditional drug therapy because it
                  employs MRI-strength magnetic pulses targeted precisely to
                  regions of the brain involved in mood regulation—specifically
                  areas like the Dorsolateral Prefrontal Cortex (DLPFC), which
                  are known to be under-active in patients diagnosed with MDD.
                </p>
                <p>
                  By stimulating these circuits, TMS raises levels of key
                  neurotransmitters—dopamine, norepinephrine, and serotonin—and
                  can be administered alongside existing medication or as a
                  stand-alone therapy.
                </p>
              </div>

              {/* SmartFocus / Precision Comparison Card */}
              <div className="rounded-2xl bg-light-blue/30 border border-light-blue p-6 sm:p-10 space-y-6">
                <div className="flex items-center gap-3 text-primary">
                  <div className="h-10 w-10 rounded-xl bg-primary text-white flex items-center justify-center font-bold">
                    <Brain className="h-5 w-5" />
                  </div>
                  <h3 className="font-headings text-xl sm:text-2xl font-bold">
                    Targeted Mood Regulation Without Medication Side Effects
                  </h3>
                </div>
                <p className="text-foreground/85">
                  Because TMS is non-invasive, no anesthesia or sedation is
                  required; patients remain alert, seated comfortably, and can
                  drive themselves back to work or home immediately after
                  sessions. Furthermore, since it does not enter the
                  bloodstream, common pharmaceutical side effects—such as weight
                  gain, fatigue, or sexual dysfunction—do not occur with TMS.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  <div className="bg-card p-4 rounded-xl border border-border shadow-xs space-y-1">
                    <span className="text-xs font-bold uppercase text-accent">
                      Clinical Trial Proof
                    </span>
                    <p className="font-bold text-primary text-lg">
                      2 out of 3 Patients
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Reported remission or significant reduction in depression
                      scores in FDA trials.
                    </p>
                  </div>
                  <div className="bg-card p-4 rounded-xl border border-border shadow-xs space-y-1">
                    <span className="text-xs font-bold uppercase text-accent">
                      Real-World Studies
                    </span>
                    <p className="font-bold text-primary text-lg">
                      3 out of 4 Patients
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Positively respond to TMS therapy in clinical practice
                      settings.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Benefits Grid */}
            <div className="space-y-6">
              <div className="text-center max-w-2xl mx-auto space-y-2">
                <h2 className="font-headings text-2xl sm:text-4xl font-extrabold text-primary">
                  Core Benefits of TMS Therapy
                </h2>
                <p className="text-muted-foreground text-sm sm:text-base">
                  Why patients and psychiatrists choose TMS over increasing
                  medication dosages.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {benefits.map((benefit, idx) => {
                  const Icon = benefit.icon;
                  return (
                    <div
                      key={idx}
                      className="rounded-2xl border border-border bg-card p-6 shadow-soft hover:shadow-premium transition-all space-y-3"
                    >
                      <div className="h-12 w-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center font-bold">
                        <Icon className="h-6 w-6" />
                      </div>
                      <h3 className="font-headings text-lg font-bold text-primary">
                        {benefit.title}
                      </h3>
                      <p className="text-sm text-foreground/80 leading-relaxed font-normal">
                        {benefit.description}
                      </p>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Insurance & Community Advocacy Box */}
            <div className="rounded-2xl bg-card border border-border p-6 sm:p-10 shadow-soft space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div className="space-y-4">
                  <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-accent">
                    <ShieldCheck className="h-4 w-4" />
                    Insurance & Financial Assistance
                  </span>
                  <h3 className="font-headings text-xl sm:text-2xl font-bold text-primary">
                    TMS Therapy Is Covered By Most Major Plans
                  </h3>
                  <p className="text-sm sm:text-base text-foreground/85 leading-relaxed font-normal">
                    The clinical intake team at Neuro Wellness TMS Centers of
                    America assists patients with complete prior authorization
                    processing, benefit verification, and out-of-network
                    options.
                  </p>
                  <div className="pt-2">
                    <Button
                      asChild
                      variant="outline"
                      className="border-accent text-accent hover:bg-accent hover:text-white"
                    >
                      <a href="#consultation">Verify Insurance Benefits</a>
                    </Button>
                  </div>
                </div>

                <div className="bg-light-blue/20 p-6 rounded-2xl border border-light-blue space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-accent text-white flex items-center justify-center font-bold text-xs">
                      TMS+
                    </div>
                    <div>
                      <h4 className="font-headings font-bold text-primary">
                        TMS + YOU Advocacy
                      </h4>
                      <p className="text-xs text-muted-foreground">
                        National Patient Community
                      </p>
                    </div>
                  </div>
                  <p className="text-xs sm:text-sm text-foreground/80 leading-relaxed">
                    TMS+YOU is an online community and national advocacy
                    organization for individuals considering Transcranial
                    Magnetic Stimulation. Connect with former patients, share
                    experiences, and explore educational resources.
                  </p>
                  <div className="pt-1">
                    <Link
                      to="/contents/tms/tms-clinical-study-results"
                      className="text-xs font-bold text-accent hover:underline inline-flex items-center gap-1"
                    >
                      Explore TMS Clinical Study Results
                      <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Sub-navigation Directory */}
            <div className="rounded-2xl bg-gradient-navy text-primary-foreground p-6 sm:p-10 space-y-6">
              <h3 className="font-headings text-xl sm:text-2xl font-bold text-white text-center">
                Explore More TMS Resources
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Link
                  to="/contents/tms/is-tms-right-for-me"
                  className="bg-white/10 hover:bg-white/20 p-4 rounded-xl border border-white/20 transition-all text-center space-y-2 group"
                >
                  <HelpCircle className="h-6 w-6 text-accent mx-auto" />
                  <p className="font-bold text-white text-sm group-hover:text-accent transition-colors">
                    Is TMS Right For Me?
                  </p>
                  <p className="text-xs text-primary-foreground/75">
                    Candidate quiz & medical guidelines
                  </p>
                </Link>

                <Link
                  to="/contents/tms/typical-tms-treatment"
                  className="bg-white/10 hover:bg-white/20 p-4 rounded-xl border border-white/20 transition-all text-center space-y-2 group"
                >
                  <Activity className="h-6 w-6 text-accent mx-auto" />
                  <p className="font-bold text-white text-sm group-hover:text-accent transition-colors">
                    Typical TMS Treatment
                  </p>
                  <p className="text-xs text-primary-foreground/75">
                    Session count, duration & motor threshold
                  </p>
                </Link>

                <Link
                  to="/contents/tms/tms-clinical-study-results"
                  className="bg-white/10 hover:bg-white/20 p-4 rounded-xl border border-white/20 transition-all text-center space-y-2 group"
                >
                  <BarChart3 className="h-6 w-6 text-accent mx-auto" />
                  <p className="font-bold text-white text-sm group-hover:text-accent transition-colors">
                    Clinical Study Results
                  </p>
                  <p className="text-xs text-primary-foreground/75">
                    Dunner, Vaché & Cress study data
                  </p>
                </Link>
              </div>
            </div>
          </div>
        </section>

        <RelatedPages
          heading="Explore TMS Therapy by Condition"
          links={allTmsByConditionLinks.filter(
            (l) => l.href !== "/contents/tms/what-is-tms-therapy",
          )}
        />
        <RelatedPages
          heading="Explore Related TMS Resources"
          links={[
            ...allEducationLinks.filter(
              (l) => l.href !== "/contents/tms/what-is-tms-therapy/",
            ),
            ...allTreatmentsLinks,
            ...allServiceLinks,
          ]}
        />

        {/* Consultation Component */}
        <Consultation />
      </main>

      <Footer />
    </div>
  );
};
export default WhatIsTms;
